import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';

import { ReferencepinloginService, LoginRequest, LoginResponse } from './referencepinlogin.service';
import { SessionService } from '../session.service';

@Component({
  selector: 'app-serial-pinlogin',
  templateUrl: './serial-pinlogin.component.html',
  styleUrls: ['./serial-pinlogin.component.scss'],
})
export class SerialPinloginComponent implements OnInit {
  @ViewChild('referenceIdInput') referenceIdInput!: ElementRef;

  referenceId: string = '';
  pinCode: string = '';

  isReferenceIdValid: boolean = false;
  isPinValid: boolean = false;
  isSubmitting: boolean = false;

  errorMessage: string = '';

  private readonly NAVIGATION_DELAY = 150; // Delay before navigation for token sync

  showSessionExpiredModal = false;

  constructor(
    private router: Router,
    private referencepinloginAuthService: ReferencepinloginService,
    private toastr: ToastrService,
    private sessionService: SessionService
  ) {}


  ngOnInit(): void {
      this.sessionService.sessionExpired$.subscribe(() => {
      this.showSessionExpiredModal = true;
     });
  setTimeout(() => {
    if (this.referencepinloginAuthService.isAuthenticated()) {
      // ✅ Also verify token is not expired before redirecting
      const token = sessionStorage.getItem('authToken');
      if (token && !this.isTokenExpired(token)) {
        console.log('✅ Valid session found, redirecting to dashboard...');
        this.router.navigateByUrl('/main-application-dashboard');
        return;
      } else {
        // ✅ Stale/expired token - clear it and stay on login
        console.log('⚠️ Stale token found, clearing...');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('userDetails');
        this.showSessionExpiredModal = true;

      }
    }
    console.log('⚠️ User not authenticated, showing login form');
  }, 100);
}

 onSessionLogin(): void {
    this.showSessionExpiredModal = false;
  }

  /**
   * Navigate back to home page
   */
  navigateToHome(): void {
    console.log('🏠 Navigating to home page...');
    
    // Show toast notification
    this.toastr.info('Redirecting to home page...', 'Navigation', {
      timeOut: 1500,
      progressBar: true
    });

    // Navigate to home route after short delay
    setTimeout(() => {
      // Update with your actual home route
      this.router.navigate(['/']).then(
        (success) => {
          if (success) {
            console.log('✅ Home navigation successful');
          } else {
            console.warn('⚠️ Home navigation failed, trying fallback...');
            // Fallback to window.location
            window.location.href = '/';
          }
        },
        (error) => {
          console.error('❌ Home navigation error:', error);
          // Fallback to window.location
          window.location.href = '/';
        }
      );
    }, 300);
  }

  onReferenceIdInput(event: any): void {
    this.referenceId = event.target.value.trim();
    this.isReferenceIdValid = this.referenceId.length >= 6;
    this.clearMessages();
  }

  onPinInput(event: any): void {
    this.pinCode = event.target.value.trim();
    this.isPinValid = this.pinCode.length >= 4;
    this.clearMessages();
  }

  clearMessages(): void {
    this.errorMessage = '';
  }

  isFormValid(): boolean {
    return this.isReferenceIdValid && this.isPinValid;
  }

  onSubmit(): void {
    console.log('=== SUBMIT BUTTON CLICKED ===');
    
    if (!this.isFormValid()) {
      console.log('❌ Form validation failed');
      this.toastr.warning('Please enter valid Reference ID and PIN', 'Validation Error');
      return;
    }

    console.log('✅ Form is valid, proceeding with login...');
    this.isSubmitting = true;
    this.clearMessages();

    const loginData: LoginRequest = {
      referenceId: this.referenceId,
      pin: this.pinCode,
    };

    console.log('📤 Login request data:', { 
      referenceId: loginData.referenceId, 
      pin: '***' 
    });

    const loadingToast = this.toastr.info('Verifying credentials...', 'Please wait', {
      disableTimeOut: true,
      closeButton: false,
      tapToDismiss: false,
    });

    this.referencepinloginAuthService
      .login(loginData)
      .pipe(
        finalize(() => {
          console.log('=== LOGIN REQUEST COMPLETED (finalize) ===');
          this.toastr.clear(loadingToast.toastId);
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (response: LoginResponse & { refreshToken?: string }) => {
          console.log('=== LOGIN SUCCESS RESPONSE ===');
          console.log('📥 Response:', response);
          
          if (response.success && response.token) {
            this.handleLoginSuccess(response);
          } else {
            console.log('❌ Login failed with success=false in response');
            const message = response.message || 'Login failed';
            this.errorMessage = message;
            this.toastr.error(message, 'Login Failed', { timeOut: 5000 });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handleLoginError(error);
        },
      });
  }

  private handleLoginSuccess(response: LoginResponse & { refreshToken?: string }): void {
    console.log('✅ Processing successful login...');
    
    // Store tokens - service already does this, but let's verify
    const token = response.token!;
    sessionStorage.setItem('authToken', token);
    
    if (response.refreshToken) {
      sessionStorage.setItem('refresh_token', response.refreshToken);
      console.log('💾 Refresh token stored');
    }

    // Store user details if available
    if (response.userDetails) {
      sessionStorage.setItem('userDetails', JSON.stringify(response.userDetails));
      console.log('💾 User details stored');
    }

    // CRITICAL: Verify token was stored before navigation
    const storedToken = sessionStorage.getItem('authToken');
    if (!storedToken) {
      console.error('❌ Token storage verification failed!');
      this.errorMessage = 'Authentication failed. Please try again.';
      this.toastr.error(this.errorMessage, 'Error');
      return;
    }

    console.log('✅ Token verified in sessionStorage');
    console.log('✅ All sessionStorage keys:', Object.keys(sessionStorage));

    // Show success message
    const userName = response.userDetails?.firstName || 'User';
    this.toastr.success(
      `Welcome back, ${userName}! Redirecting...`,
      'Login Successful',
      { timeOut: 2000 }
    );

    console.log(`🔄 Navigating to dashboard in ${this.NAVIGATION_DELAY}ms...`);
    
    // CRITICAL: Use setTimeout to ensure sessionStorage is fully synchronized
    setTimeout(() => {
      console.log('🚀 Starting navigation to /main-application-dashboard');
      console.log('🔍 Pre-navigation token check:', !!sessionStorage.getItem('authToken'));
      
      this.router.navigate(['/main-application-dashboard']).then(
        (success) => {
          if (success) {
            console.log('✅ Navigation successful');
          } else {
            console.error('❌ Navigation failed - possibly blocked by guard');
            // Fallback to window.location if Angular navigation fails
            window.location.hash = '#/main-application-dashboard';
          }
        },
        (error) => {
          console.error('❌ Navigation error:', error);
          // Fallback
          window.location.hash = '#/main-application-dashboard';
        }
      );
    }, this.NAVIGATION_DELAY);
  }

  private handleLoginError(error: HttpErrorResponse): void {
    console.log('=== LOGIN ERROR RESPONSE ===');
    console.log('❌ Full error object:', error);
    console.log('❌ Error status:', error.status);
    console.log('❌ Error body:', error.error);
    console.log('❌ Error message:', error.message);
    
    // CRITICAL: Clear any stale tokens
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('userDetails');
    console.log('🧹 Cleared stale tokens from sessionStorage');
    
    let displayMessage = 'An unexpected error occurred. Please try again later.';
    
    // Handle different error statuses
    if (error.status === 401) {
      console.log('❌ 401 Unauthorized error');
      displayMessage = error.error?.message || 'Invalid Reference ID or PIN. Please check your credentials.';
    } else if (error.status === 400) {
      console.log('❌ 400 Bad Request error');
      displayMessage = error.error?.message || 'Invalid request. Please check your input.';
    } else if (error.status === 500) {
      console.log('❌ 500 Server error');
      displayMessage = error.error?.message || 'Server error occurred. Please try again later.';
    } else if (error.status === 0) {
      console.log('❌ Network error (status 0)');
      displayMessage = 'Unable to connect to server. Please check your internet connection.';
    } else if (error.error?.message) {
      console.log('❌ Error with message in body');
      displayMessage = error.error.message;
    }
    
    console.log('📢 Display message:', displayMessage);
    this.errorMessage = displayMessage;
    this.toastr.error(displayMessage, 'Login Failed', { timeOut: 7000 });
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.isFormValid() && !this.isSubmitting) {
      this.onSubmit();
    }
  }



  private isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= payload.exp * 1000;
  } catch (e) {
    return true;
  }
}

}